/**
 * 🗺️ [Phase 99 — 2026-07-09] + 🚨 [HOTFIX Google Maps — 2026-07-13]
 * Composant réutilisable : Google Places Autocomplete
 *
 * Utilise l'API JavaScript Google Places (loader dynamique) pour offrir un
 * autocomplete d'adresses. Retourne à onChange :
 *   { adresse_complete, latitude, longitude, place_id, ville, code_postal, pays }
 *
 * Fallback : si l'API ne charge pas OU si `window.gm_authFailure` est déclenché
 * (clé invalide, referer refusé, facturation absente, quota dépassé), on tombe
 * en simple <Input> texte libre. La saisie est TOUJOURS enregistrée dans
 * `adresse_complete`, permettant la rétro-compat avec les vieilles adresses
 * stockées sans coordonnées.
 *
 * HOTFIX 2026-07-13 :
 *  - Écoute globale `window.gm_authFailure` → bascule fallback + log backend
 *  - Suppression des overlays intrusifs Google Maps (`.gm-err-container`)
 *  - Log erreurs vers `POST /api/frontend-errors/google-maps` (best-effort)
 *
 * Usage :
 *   <GooglePlacesAutocomplete
 *     value={adresse}
 *     onChange={(data) => setAdresse(data.adresse_complete)}
 *     onPlaceSelected={(place) => setDetails({lat: place.latitude, ...})}
 *     placeholder="Ex : 123 rue Principale, Québec"
 *     dataTestId="event-adresse-input"
 *   />
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const LIBRARIES = 'places';
const SCRIPT_ID = 'google-places-script';

// Cache le loading state pour éviter multi-injections du script.
let _googleMapsLoadingPromise = null;

// 🚨 [HOTFIX 2026-07-13] État global mis à true quand Google Maps échoue
// (clé invalide, referer non autorisé, facturation absente, quota).
// Une fois true, TOUS les composants montés basculent en fallback dégradé.
let _googleMapsAuthFailed = false;
const _authFailureListeners = new Set();

/**
 * Log best-effort d'une erreur Google Maps vers le backend.
 * Non bloquant, ne throw jamais.
 */
function reportGoogleMapsError(errorType, errorMessage) {
  try {
    if (!BACKEND_URL) return;
    axios
      .post(`${BACKEND_URL}/api/frontend-errors/google-maps`, {
        error_type: errorType,
        error_message: String(errorMessage || '').slice(0, 500),
        url: typeof window !== 'undefined' ? window.location.href : '',
        timestamp: new Date().toISOString(),
      })
      .catch(() => {
        /* silencieux — best-effort */
      });
  } catch {
    /* silencieux */
  }
}

/**
 * Notifie tous les composants montés qu'un `gm_authFailure` s'est produit.
 * Utilisé aussi bien par le callback global Google que par un onerror script.
 */
function _broadcastAuthFailure() {
  _googleMapsAuthFailed = true;
  _authFailureListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* noop */
    }
  });
}

/**
 * Installe UNE SEULE FOIS le callback global `window.gm_authFailure`
 * appelé par Google Maps quand l'authentification échoue (clé invalide,
 * referer refusé, facturation absente, quota dépassé).
 */
function installGmAuthFailureListener() {
  if (typeof window === 'undefined') return;
  if (window.__prqcGmAuthFailureInstalled) return;
  window.__prqcGmAuthFailureInstalled = true;

  window.gm_authFailure = () => {
    console.warn(
      '[GooglePlacesAutocomplete] gm_authFailure — bascule fallback texte libre.'
    );
    reportGoogleMapsError(
      'gm_authFailure',
      'Google Maps authentication failed (referer/key/billing/quota)'
    );
    _broadcastAuthFailure();
  };
}

function loadGooglePlacesScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR context'));
  installGmAuthFailureListener();
  if (_googleMapsAuthFailed) {
    return Promise.reject(new Error('gm_authFailure déjà détecté'));
  }
  if (window.google && window.google.maps && window.google.maps.places) {
    return Promise.resolve(window.google);
  }
  if (_googleMapsLoadingPromise) return _googleMapsLoadingPromise;
  if (!API_KEY) {
    return Promise.reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY manquant'));
  }

  _googleMapsLoadingPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(API_KEY)}&libraries=${LIBRARIES}&language=fr`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = (err) => {
      _googleMapsLoadingPromise = null; // permet retry
      reportGoogleMapsError('script_load_error', 'Failed to load Google Maps JS');
      reject(err);
    };
    document.head.appendChild(script);
  });
  return _googleMapsLoadingPromise;
}

export function GooglePlacesAutocomplete({
  value = '',
  onChange,
  onPlaceSelected,
  placeholder = 'Rechercher une adresse...',
  dataTestId,
  disabled,
  className,
  countryRestriction = ['ca', 'us'],
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [libStatus, setLibStatus] = useState('idle'); // idle | loading | ready | error

  useEffect(() => {
    let mounted = true;

    // 🚨 [HOTFIX 2026-07-13] S'abonner au broadcast global gm_authFailure
    const onAuthFailure = () => {
      if (!mounted) return;
      setLibStatus('error');
    };
    _authFailureListeners.add(onAuthFailure);

    // Si déjà en échec au moment du mount, bascule immédiatement
    if (_googleMapsAuthFailed) {
      setLibStatus('error');
      return () => {
        mounted = false;
        _authFailureListeners.delete(onAuthFailure);
      };
    }

    setLibStatus('loading');
    loadGooglePlacesScript()
      .then(() => {
        if (!mounted) return;
        setLibStatus('ready');
      })
      .catch((err) => {
        if (!mounted) return;
        console.warn('[GooglePlacesAutocomplete] fallback texte libre :', err?.message || err);
        setLibStatus('error');
      });

    return () => {
      mounted = false;
      _authFailureListeners.delete(onAuthFailure);
    };
  }, []);

  useEffect(() => {
    if (libStatus !== 'ready' || !inputRef.current) return;
    if (autocompleteRef.current) return; // déjà initialisé

    const google = window.google;
    if (!google?.maps?.places) return;

    autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country: countryRestriction },
      fields: ['formatted_address', 'geometry', 'place_id', 'address_components'],
    });

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (!place || !place.formatted_address) return;

      const details = {
        adresse_complete: place.formatted_address,
        latitude: place.geometry?.location?.lat?.() ?? null,
        longitude: place.geometry?.location?.lng?.() ?? null,
        place_id: place.place_id || null,
        ville: '',
        code_postal: '',
        pays: '',
      };

      for (const comp of place.address_components || []) {
        const types = comp.types || [];
        if (types.includes('locality')) details.ville = comp.long_name;
        else if (types.includes('postal_code')) details.code_postal = comp.long_name;
        else if (types.includes('country')) details.pays = comp.long_name;
      }

      onChange?.(details.adresse_complete);
      onPlaceSelected?.(details);
    });

    return () => {
      if (autocompleteRef.current && google?.maps?.event) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      autocompleteRef.current = null;
    };
  }, [libStatus, onChange, onPlaceSelected, countryRestriction]);

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        data-testid={dataTestId}
        autoComplete="off"
      />
      {libStatus === 'loading' && (
        <p className="text-xs text-slate-400 mt-1" data-testid="google-places-loading">
          Chargement de l&apos;autocomplete...
        </p>
      )}
      {libStatus === 'error' && (
        <p
          className="text-xs text-amber-600 mt-1"
          data-testid="google-places-fallback"
          title="L'autocomplétion Google est temporairement indisponible — saisissez l'adresse librement."
        >
          Saisie libre (autocomplétion Google temporairement indisponible)
        </p>
      )}
    </div>
  );
}

// 🚨 [HOTFIX 2026-07-13] Exports helpers pour tests unitaires
export const __TESTING__ = {
  reportGoogleMapsError,
  installGmAuthFailureListener,
  getAuthFailedState: () => _googleMapsAuthFailed,
  resetAuthFailedState: () => {
    _googleMapsAuthFailed = false;
  },
  broadcastAuthFailure: _broadcastAuthFailure,
};

export default GooglePlacesAutocomplete;
